import BGImg3 from "../../assets/img/bg/ap-bg.png";
import emailjs from "emailjs-com";
import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { SERVICE_ID, TEMPLATE_ID, PUBLIC_KEY } from "../../config/emailjs";
import { WIGS_ENABLED } from "../../config/features.mjs";

const appointmentStyles = `
  @media (max-width: 767px) {
    .ap-info-panel { order: 2; }
    .ap-form-card  { order: 1; }
  }
`;

const modalStyles = `
  @keyframes fadeInScale {
    from { opacity: 0; transform: scale(0.92) translateY(12px); }
    to   { opacity: 1; transform: scale(1)    translateY(0); }
  }
  @keyframes backdropFadeIn {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
  .booking-modal-backdrop {
    animation: backdropFadeIn 0.25s ease forwards;
  }
  .booking-modal-card {
    animation: fadeInScale 0.35s cubic-bezier(0.34,1.26,0.64,1) forwards;
  }
`;

const SuccessModal = ({ onClose }) => {
  const { t } = useTranslation();
  return (
    <>
      <style>{modalStyles}</style>
      <div
        className="booking-modal-backdrop"
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 9999,
          background: "rgba(10,5,2,0.72)",
          backdropFilter: "blur(6px)",
          WebkitBackdropFilter: "blur(6px)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px",
        }}
      >
        <div
          className="booking-modal-card"
          onClick={(e) => e.stopPropagation()}
          style={{
            background: "#fff",
            borderRadius: "28px",
            padding: "52px 48px",
            maxWidth: "460px",
            width: "100%",
            textAlign: "center",
            boxShadow: "0 32px 80px rgba(0,0,0,0.28)",
            border: "1px solid rgba(144,81,15,0.12)",
            position: "relative",
          }}
        >
          <button
            onClick={onClose}
            aria-label={t('appointment.modal.closeAria')}
            style={{
              position: "absolute",
              top: "18px",
              right: "20px",
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "#C4B3A0",
              lineHeight: 1,
              fontSize: "20px",
              padding: "4px",
            }}
          >
            ✕
          </button>

          <div
            style={{
              width: "72px",
              height: "72px",
              borderRadius: "50%",
              background: "rgba(144,81,15,0.10)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 24px",
            }}
          >
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#90510F" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>

          <p
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: "11px",
              fontWeight: "700",
              letterSpacing: "0.22em",
              textTransform: "uppercase",
              color: "#D4A96A",
              marginBottom: "12px",
            }}
          >
            {t('appointment.modal.eyebrow')}
          </p>

          <h2
            style={{
              fontFamily: "'Cormorant Garamond', 'PT Serif', serif",
              fontSize: "28px",
              fontWeight: "600",
              color: "#1A0D05",
              letterSpacing: "-0.01em",
              lineHeight: "1.2",
              marginBottom: "16px",
            }}
          >
            {t('appointment.modal.title')}
          </h2>

          <p
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: "15px",
              lineHeight: "1.75",
              color: "#5A4A3A",
              marginBottom: "32px",
            }}
          >
            {t('appointment.modal.message')}
          </p>

          <div
            style={{
              width: "40px",
              height: "2px",
              background: "#90510F",
              borderRadius: "2px",
              margin: "0 auto 28px",
            }}
          />

          <button
            onClick={onClose}
            style={{
              display: "inline-block",
              padding: "15px 44px",
              background: "#90510F",
              color: "#fff",
              fontFamily: "'Inter', sans-serif",
              fontSize: "13px",
              fontWeight: "700",
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              borderRadius: "9999px",
              border: "none",
              cursor: "pointer",
              boxShadow: "0 8px 24px rgba(144,81,15,0.32)",
              transition: "background 0.25s ease, transform 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#6b3b08";
              e.currentTarget.style.transform = "translateY(-2px)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "#90510F";
              e.currentTarget.style.transform = "translateY(0)";
            }}
          >
            {t('appointment.modal.closeBtn')}
          </button>
        </div>
      </div>
    </>
  );
};

const inputStyle = {
  width: "100%",
  padding: "15px 20px",
  borderRadius: "14px",
  border: "1.5px solid rgba(144,81,15,0.18)",
  fontFamily: "'Inter', sans-serif",
  fontSize: "14px",
  color: "#1A0D05",
  background: "#fff",
  outline: "none",
  transition: "border-color 0.3s ease, box-shadow 0.3s ease",
  boxSizing: "border-box",
  marginBottom: "0",
};

const inputFocusStyle = {
  borderColor: "rgba(144,81,15,0.50)",
  boxShadow: "0 0 0 3.5px rgba(144,81,15,0.09)",
};

const FocusInput = ({ style, ...props }) => {
  const [focused, setFocused] = useState(false);
  return (
    <input
      {...props}
      style={{ ...inputStyle, ...(focused ? inputFocusStyle : {}), ...style }}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    />
  );
};

const FocusTextarea = ({ style, ...props }) => {
  const [focused, setFocused] = useState(false);
  return (
    <textarea
      {...props}
      style={{
        ...inputStyle,
        height: "130px",
        resize: "vertical",
        ...(focused ? inputFocusStyle : {}),
        ...style,
      }}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    />
  );
};

const FocusSelect = ({ style, children, ...props }) => {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ position: "relative" }}>
      <select
        {...props}
        style={{
          ...inputStyle,
          appearance: "none",
          WebkitAppearance: "none",
          paddingRight: "44px",
          cursor: "pointer",
          ...(focused ? inputFocusStyle : {}),
          ...style,
        }}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
      >
        {children}
      </select>
      <svg
        style={{
          position: "absolute",
          right: "16px",
          top: "50%",
          transform: "translateY(-50%)",
          pointerEvents: "none",
        }}
        width="12"
        height="8"
        viewBox="0 0 12 8"
        fill="none"
      >
        <path d="M1 1l5 5 5-5" stroke="#90510F" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    </div>
  );
};

/* Course enrolments have no appointment slot to pick - the group start date
   is fixed and the advanced course is arranged individually. Selecting one of
   these disables the date field instead of asking for a meaningless time. */
const COURSE_SERVICES = ["Bazni kurs šminkanja", "Napredni kurs šminkanja"];

/**
 * @param {string} [defaultService]
 *        Pre-selects an entry in the service list. The hair pages pass their
 *        own service so the enquiry arrives saying what it is about, instead
 *        of the visitor having to pick the nearest match.
 */
const Appointment = ({ defaultService = "" }) => {
  const { t } = useTranslation();
  const form = useRef();
  const [submitted, setSubmitted] = useState(false);
  const [service, setService] = useState(defaultService);
  const isCourse = COURSE_SERVICES.includes(service);

  const sendEmail = (e) => {
    e.preventDefault();

    if (!isCourse) {
      const selectedDate = new Date(form.current.date.value);
      const now = new Date();

      if (selectedDate < now) {
        alert(t('appointment.alert.pastDate'));
        return;
      }
    }

    emailjs
      .sendForm(
        SERVICE_ID,
        TEMPLATE_ID,
        form.current,
        PUBLIC_KEY
      )
      .then(
        (result) => {
          console.log(result.text);
          setSubmitted(true);
          form.current.reset();
        },
        (error) => {
          console.error(error.text);
          alert(t('appointment.alert.error'));
        }
      );
  };

  return (
    <>
      <style>{appointmentStyles}</style>
      {submitted && <SuccessModal onClose={() => setSubmitted(false)} />}
    <section
      id="booking"
      style={{
        backgroundImage: `url(${BGImg3})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        padding: "100px 0",
        position: "relative",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: "rgba(10,5,2,0.45)",
          pointerEvents: "none",
        }}
      />

      <div
        style={{
          maxWidth: "1180px",
          margin: "0 auto",
          padding: "0 24px",
          display: "flex",
          flexWrap: "wrap",
          gap: "48px",
          alignItems: "center",
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* Left info panel */}
        <div
          className="ap-info-panel"
          style={{
            flex: "1 1 340px",
            minWidth: "280px",
            textAlign: "center",
          }}
        >
          <p
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: "11px",
              fontWeight: "700",
              letterSpacing: "0.24em",
              textTransform: "uppercase",
              color: "#D4A96A",
              display: "inline-flex",
              alignItems: "center",
              gap: "10px",
              marginBottom: "20px",
            }}
          >
            <span style={{ display: "inline-block", width: "28px", height: "1.5px", background: "#D4A96A", borderRadius: "2px" }} />
            {t('appointment.eyebrow')}
            <span style={{ display: "inline-block", width: "28px", height: "1.5px", background: "#D4A96A", borderRadius: "2px" }} />
          </p>

          <h2
            style={{
              fontFamily: "'Cormorant Garamond', 'PT Serif', serif",
              fontSize: "clamp(2rem, 4vw, 3rem)",
              fontWeight: "600",
              letterSpacing: "-0.01em",
              lineHeight: "1.15",
              marginBottom: "20px",
              color: "#FAF7F2",
            }}
          >
            {t('appointment.heading')}
          </h2>

          <p
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: "15px",
              lineHeight: "1.8",
              marginBottom: "24px",
              color: "rgba(255,255,255,0.80)",
              padding: "0 8px",
            }}
          >
            {t('appointment.phoneText')}{" "}
            <a
              href="tel:0653585545"
              style={{
                color: "#D4A96A",
                fontWeight: "600",
                textDecoration: "none",
              }}
            >
              065/3585-545
            </a>{" "}
            {t('appointment.phoneTextAfter')}
          </p>

          <p
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: "15px",
              lineHeight: "1.8",
              marginBottom: "32px",
              color: "rgba(255,255,255,0.72)",
              fontStyle: "italic",
            }}
          >
            {t('appointment.quote')}
          </p>

          <a
            href="tel:0653585545"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "10px",
              padding: "16px 36px",
              background: "#90510F",
              color: "#fff",
              fontFamily: "'Inter', sans-serif",
              fontWeight: "700",
              fontSize: "13px",
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              borderRadius: "9999px",
              textDecoration: "none",
              boxShadow: "0 8px 28px rgba(144,81,15,0.38)",
              transition: "background 0.3s ease, transform 0.2s ease, box-shadow 0.3s ease",
              marginBottom: "32px",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#6b3b08";
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = "0 12px 36px rgba(144,81,15,0.50)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "#90510F";
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 8px 28px rgba(144,81,15,0.38)";
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.8a19.79 19.79 0 01-3.07-8.68A2 2 0 012 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 14.92z"/>
            </svg>
            {t('appointment.callNow')}
          </a>

          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "12px" }}>
            <p style={{ fontFamily: "'Inter', sans-serif", fontSize: "13px", color: "rgba(255,255,255,0.65)", margin: 0 }}>
              {t('appointment.socialText')}
            </p>
            <div style={{ display: "flex", gap: "16px" }}>
              {[
                { href: "https://www.facebook.com/js.makeup.35/",     label: "Facebook"  },
                { href: "https://www.instagram.com/visionbeauty.rs/", label: "Instagram" },
              ].map(({ href, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "10px 20px",
                    background: "rgba(255,255,255,0.10)",
                    backdropFilter: "blur(8px)",
                    border: "1px solid rgba(255,255,255,0.18)",
                    borderRadius: "9999px",
                    color: "#fff",
                    fontFamily: "'Inter', sans-serif",
                    fontSize: "13px",
                    fontWeight: "600",
                    textDecoration: "none",
                    transition: "background 0.25s ease",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.18)")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.10)")}
                >
                  {label}
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Form card */}
        <form
          ref={form}
          onSubmit={sendEmail}
          className="ap-form-card"
          style={{
            flex: "1 1 440px",
            minWidth: "300px",
            background: "rgba(255,255,255,0.94)",
            backdropFilter: "blur(16px)",
            WebkitBackdropFilter: "blur(16px)",
            borderRadius: "28px",
            padding: "44px",
            boxShadow: "0 24px 72px rgba(0,0,0,0.16)",
            border: "1px solid rgba(144,81,15,0.10)",
          }}
        >
          <h3
            style={{
              fontFamily: "'Cormorant Garamond', 'PT Serif', serif",
              fontSize: "26px",
              fontWeight: "600",
              color: "#1A0D05",
              marginBottom: "8px",
              letterSpacing: "-0.01em",
            }}
          >
            {t('appointment.formHeading')}
          </h3>
          <p
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: "13px",
              color: "#8B7B6B",
              marginBottom: "28px",
              lineHeight: "1.6",
            }}
          >
            {t('appointment.formSubtext')}
          </p>

          <div style={{ width: "40px", height: "2px", background: "#90510F", borderRadius: "2px", marginBottom: "28px" }} />

          <div className="row">
            <div className="col-lg-6" style={{ marginBottom: "18px" }}>
              <FocusInput type="text" name="from_name" placeholder={t('appointment.fields.name')} required />
            </div>

            <div className="col-lg-6" style={{ marginBottom: "18px" }}>
              <FocusInput type="email" name="from_email" placeholder={t('appointment.fields.email')} required />
            </div>

            <div className="col-lg-6" style={{ marginBottom: "18px" }}>
              <FocusInput type="tel" name="phone" placeholder={t('appointment.fields.phone')} required />
            </div>

            <div className="col-lg-6" style={{ marginBottom: "18px" }}>
              <FocusSelect
                name="service"
                required
                value={service}
                onChange={(e) => setService(e.target.value)}
              >
                <option value="">{t('appointment.fields.serviceLabel')}</option>
                <option value="Profesionalna šminka">{t('appointment.services.makeup')}</option>
                <option value="Frizura">{t('appointment.services.hairstyle')}</option>
                <option value="Trepavice">{t('appointment.services.eyelashes')}</option>
                <option value="Vega obrve">{t('appointment.services.vega')}</option>
                <option value="Manikir-pedikir">{t('appointment.services.nails')}</option>
                <option value="Baby lips">{t('appointment.services.babyLips')}</option>
                <option value="Depilacija">{t('appointment.services.depilation')}</option>
                {/* Values stay Serbian in every language so the inbox always
                    reads the same - same convention as the services above. */}
                <option value="Farbanje kose">{t('nav.svcColouring')}</option>
                <option value="Balayage">{t('nav.svcBalayage')}</option>
                <option value="Tretman kose">{t('nav.svcTreatments')}</option>
                <option value="Muško šišanje">{t('nav.svcMensHaircut')}</option>
                <option value="Ekstenzije za kosu">{t('nav.svcExtensions')}</option>
                {/* Wigs are not in stock yet - see src/config/features.mjs. */}
                {WIGS_ENABLED && <option value="Perike">{t('nav.svcWigs')}</option>}
                <option value="Bazni kurs šminkanja">{t('courseForm.courses.basic')}</option>
                <option value="Napredni kurs šminkanja">{t('courseForm.courses.advanced')}</option>
              </FocusSelect>
            </div>

            <div className="col-lg-12" style={{ marginBottom: "18px" }}>
              <div style={{ position: "relative" }}>
                {/* Disabled fields are not submitted, so `date` simply arrives
                    empty for a course enrolment. */}
                <FocusInput
                  type="datetime-local"
                  name="date"
                  required={!isCourse}
                  disabled={isCourse}
                  min={new Date().toISOString().slice(0, 16)}
                  style={{
                    paddingRight: "44px",
                    ...(isCourse ? { background: "#F3EFEA", color: "#A2968A", cursor: "not-allowed" } : {}),
                  }}
                />
                <svg
                  style={{ position: "absolute", right: "16px", top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}
                  width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#90510F" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
                >
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                  <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
              </div>
            </div>

            <div className="col-lg-12" style={{ marginBottom: "24px" }}>
              <FocusTextarea name="message" placeholder={t('appointment.fields.comment')} />
            </div>

            <div className="col-lg-12">
              <button
                type="submit"
                style={{
                  width: "100%",
                  padding: "17px 36px",
                  background: "#90510F",
                  color: "#fff",
                  fontFamily: "'Inter', sans-serif",
                  fontSize: "13px",
                  fontWeight: "700",
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  borderRadius: "9999px",
                  border: "none",
                  cursor: "pointer",
                  boxShadow: "0 8px 24px rgba(144,81,15,0.30)",
                  transition: "background 0.3s ease, transform 0.2s ease, box-shadow 0.3s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#6b3b08";
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow = "0 12px 32px rgba(144,81,15,0.44)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "#90510F";
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 8px 24px rgba(144,81,15,0.30)";
                }}
              >
                {t('appointment.submit')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </section>
    </>
  );
};

export default Appointment;
